import { Routes } from '@angular/router';
import { roles } from '@shared/constants';
import { ClientApps } from '@shared/models';
import { authorizeGuard, roleGuard } from '@shared/services/guards';
import { lendingTypeResolver } from '@shared/services/lending-type.resolver';

export const routes: Routes = [
  {
    path: '',
    loadComponent: () => import('./pages/admin-home/admin-home.component').then(m => m.AdminHomeComponent),
    resolve: { lendingTypes: lendingTypeResolver },
    canActivate: [authorizeGuard, roleGuard],
    data: { roles: [roles.adminDashboard] },
  },
  {
    path: 'procfeemanagement',
    canActivate: [authorizeGuard, roleGuard],
    data: { roles: [roles.procFeeManagement] },
    children: [
      {
        path: '',
        loadComponent: () => import('./pages/proc-fee-management/proc-fee-management.component').then(m => m.ProcFeeManagementComponent),
      },
      {
        path: 'editprocfee/:paymentRouteId/:paymentRouteName',
        loadComponent: () =>
          import('./pages/proc-fee-management/edit-proc-fee/edit-proc-fee.component').then(m => m.EditProcFeeComponent),
      },
      {
        path: 'editmapping/:paymentRouteId/:paymentRouteName',
        loadComponent: () => import('./pages/proc-fee-management/edit-mapping/edit-mapping.component').then(m => m.EditMappingComponent),
      },
      {
        path: 'auditlog/:paymentRouteId/:paymentRouteName',
        loadComponent: () => import('./pages/proc-fee-management/audit-log/audit-log.component').then(m => m.AuditLogComponent),
      },
      {
        path: 'bulkprocfeestemplates',
        loadComponent: () => import('./pages/bulk-procfee-templates/bulk-procfee-templates.component')
          .then(m => m.BulkProcFeesTemplatesComponent),
        canActivate: [authorizeGuard, roleGuard],
        data: { roles: [roles.siteManagement] },
      },
    ],
  },
  {
    path: 'BrokerLicenceAdmin',
    canActivate: [authorizeGuard, roleGuard],
    data: { roles: [roles.brokerLicenceAdmin] },
    loadComponent: () => import('./pages/broker-licence-management/broker-licence-management.component')
      .then(m => m.BrokerLicenceManagementComponent),
  },
  {
    path: 'accounts/users',
    loadComponent: () => import('./pages/users-page/users.component').then(m => m.UsersComponent),
    canActivate: [authorizeGuard, roleGuard],
    data: { roles: [
      roles.clubHubUserManagement, roles.clubHubUserManagementLimited,
      roles.lenderHubUserManagement, roles.adminDashboardUserManagement,
    ] },
  },
  {
    path: 'accounts/adduser',
    loadComponent: () => import('./pages/add-user/add-user.component').then(m => m.AddUserComponent),
    canActivate: [authorizeGuard, roleGuard],
    data: { roles: [roles.adminDashboardUserManagement] },
  },
  {
    path: 'accounts/externalclients',
    loadComponent: () => import('./pages/external-clients-page/external-clients.component').then(m => m.ExternalClientsComponent),
    canActivate: [authorizeGuard, roleGuard],
    data: { roles: [roles.externalClientManagement] },
  },
  {
    path: 'accounts/createtoken',
    loadComponent: () => import('./pages/create-token/create-token.component').then(m => m.CreateTokenComponent),
    canActivate: [authorizeGuard, roleGuard],
    data: { roles: [roles.externalClientManagement] },
  },
  {
    path: 'accounts/useraudit',
    loadComponent: () => import('./pages/user-audit-log-page/user-audit-log.component').then(m => m.UserAuditLogComponent),
    canActivate: [authorizeGuard, roleGuard],
    data: { roles: [roles.adminDashboardUserManagement] },
  },
  {
    path: 'accounts/temporarytokenaudit',
    loadComponent: () => import('./pages/temporary-token-audit-log-page/temporary-token-audit-log-page.component')
      .then(m => m.TemporaryTokenAuditLogComponent),
    canActivate: [authorizeGuard, roleGuard],
    data: { roles: [roles.adminDashboardUserManagement] },
  },
  {
    path: 'ignite/criteriaV2/landing',
    loadChildren: () => import('./criteria/criteria.module').then((m) => m.CriteriaModule),
  },
  {
    path: 'formmanagement',
    loadChildren:
      () => import('./pages/criteria-v2/criteria-v2.module').then((m) => m.CriteriaV2Module),
    resolve: { lendingTypes: lendingTypeResolver },
    canActivate: [authorizeGuard, roleGuard],
    data: { roles: [roles.formManagement], client: ClientApps.UserManager },
  },
  {
    path: 'setpassword',
    loadComponent: () => import('./pages/set-password/set-password.component').then(m => m.SetPasswordComponent),
    canActivate: [authorizeGuard, roleGuard],
    data: { roles: [roles.adminDashboard] },
  },
  {
    path: 'groupmanagement',
    children: [
      {
        path: 'distributionGroup',
        loadComponent: () => import('./pages/distribution-group-management/distribution-group-management.component').then(m => m.DistributionGroupManagementComponent),
        canActivate: [authorizeGuard, roleGuard],
        data: { roles: [roles.distributionGroupManagement] },
      },
      {
        path: 'exclusives',
        loadComponent: () => import('./pages/exclusive-products/exclusive-products-container/exclusive-products-container.component')
          .then(m => m.ExclusiveProductsContainerComponent),
        canActivate: [authorizeGuard, roleGuard],
        data: { roles: [roles.siteManagement] },
      },
      {
        path: 'exclusivesAuditLog',
        loadComponent: () => import('./pages/exclusive-product-audit-log/exclusive-product-audit-log.component')
          .then(m => m.ExclusiveProductAuditLogComponent),
        canActivate: [authorizeGuard, roleGuard],
        data: { roles: [roles.siteManagement] },
      },
    ],
  },
  {
    path: 'analytics/overview',
    loadComponent: () => import('./pages/analytics/analytics.component').then(m => m.AnalyticsComponent),
    canActivate: [authorizeGuard, roleGuard],
    data: { roles: [roles.analytics] },
  },
  {
    path: 'analytics/lender/:lenderName',
    loadComponent: () => import('./pages/analytics/details/analytics-detailed.component').then(m => m.AnalyticsDetailedComponent),
    canActivate: [authorizeGuard, roleGuard],
    data: { roles: [roles.analytics] },
  },
  {
    path: 'analytics/affordability-search-audit',
    loadComponent: () => import('./pages/affordability-search-audit/affordability-search-audit.component')
      .then(m => m.AffordabilitySearchAuditComponent),
    canActivate: [authorizeGuard, roleGuard],
    data: { roles: [roles.analytics] },
  },
  {
    path: 'analytics/service-limits',
    loadComponent: () => import('./pages/service-limits/service-limits.component')
      .then(m => m.ServiceLimitsDashboardComponent),
    canActivate: [authorizeGuard, roleGuard],
    data: { roles: [roles.analytics] },
  },
  {
    path: 'diagnostics/playwright-ui-test-reports',
    loadComponent: () => import('./pages/playwright-ui-test-reports/playwright-ui-test-reports.component').then(m => m.PlaywrightUiTestReportsComponent),
    canActivate: [authorizeGuard, roleGuard],
    data: { roles: [roles.adminDashboard] },
  },
  {
    path: 'diagnostics/screenscrapetests',
    loadComponent: () => import('./pages/screenscrape-tests/overview/screenscrape-tests-overview.component')
      .then(m => m.ScreenscrapeTestsOverviewComponent),
    canActivate: [authorizeGuard],
    data: { roles: [roles.screenscrapeTesting] },
  },
  {
    path: 'diagnostics/screenscrapetests/lender/:lendername/:lendingTypeId',
    loadComponent: () => import('./pages/screenscrape-tests/details/screenscrape-test-details.component')
      .then(m => m.ScreenscrapeTestDetailsComponent),
    canActivate: [authorizeGuard],
    data: { roles: [roles.screenscrapeTesting] },
  },
  {
    path: 'diagnostics/webjobs',
    loadComponent: () => import('./pages/web-jobs/jobs-overview/jobs-overview.component').then(m => m.WebJobsOverviewComponent),
    canActivate: [authorizeGuard, roleGuard],
    data: { roles: [roles.adminDashboard] },
  },
  {
    path: 'diagnostics/webjobs/job/:jobid',
    loadComponent: () => import('./pages/web-jobs/job-runs-overview/job-runs-overview.component')
      .then(m => m.WebJobRunsOverviewComponent),
    canActivate: [authorizeGuard, roleGuard],
    data: { roles: [roles.adminDashboard] },
  },
  {
    path: 'diagnostics/webjobs/job/:jobid/run/:runid',
    loadComponent: () => import('./pages/web-jobs/job-run-details/job-run-details.component').then(m => m.WebJobRunDetailsComponent),
    canActivate: [authorizeGuard, roleGuard],
    data: { roles: [roles.adminDashboard] },
  },
  {
    path: 'site_management/onsdataupload',
    loadComponent: () => import('./pages/ons-data-upload/ons-data-upload.component').then(m => m.OnsDataUploadComponent),
    canActivate: [authorizeGuard, roleGuard],
    data: { roles: [roles.siteManagement] },
  },
  {
    path: 'site_management/upload-fca-firm-data',
    loadComponent: () => import('./pages/upload-fca-firm-data/upload-fca-firm-data.component').then(m => m.UploadFcaFirmDataComponent),
    canActivate: [authorizeGuard, roleGuard],
    data: { roles: [roles.siteManagement] },
  },
  {
    path: 'site_management/firm-directory',
    loadComponent: () => import('./pages/firm-directory/firm-directory.component').then(m => m.FirmDirectoryComponent),
    canActivate: [authorizeGuard, roleGuard],
    data: { roles: [roles.siteManagement] },
  },
  {
    path: 'site_management/email-templates',
    loadComponent: () => import('./pages/email-templates/email-templates.component').then(m => m.EmailTemplatesComponent),
    canActivate: [authorizeGuard, roleGuard],
    data: { roles: [roles.siteManagement] },
  },
  {
    path: 'ui-component-catalogue',
    loadComponent: () => import('./pages/ui-component-catalogue/ui-component-catalogue.component')
      .then(m => m.UiComponentCatalogueComponent),
    canActivate: [authorizeGuard],
  },
  {
    path: 'error',
    loadComponent: () => import('@shared/pages/error-page/error-page.component').then(m => m.ErrorPageComponent),
  },
];
